import React, { useState, useEffect } from "react";
import MasterCard from "examples/Cards/MasterCard";
import { Card, Chip, Dialog, Grid, IconButton, Stack, Tooltip, Typography, DialogActions, DialogContent, DialogTitle, Box, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";
import SalaryComponentChart from "../components/SalaryComponent";
import ApiCalls from "services/flick_erp_api_call";
import DataTable from "examples/Tables/DataTable";
import { CheckCircle, Download, Info, Visibility, VisibilityOff } from "@mui/icons-material";
import { authFetch } from "services";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

function SalaryAccounts(props) {

	const loggedUser = JSON.parse(localStorage.getItem("user"));
	const [employeeContract, setEmployeeContract] = useState({});
	const [accountNumber, setAccountNumber] = useState();
	const [salaryLineDialog, setSalaryLineDialog] = useState(false);
	const [salaryDetails, setSalaryDetails] = useState(false);
	const [salaryRows, setSalaryRows] = useState([]);
	const [salaryHeads, setSalaryHeads] = useState([]);
	const [bankAccounts, setBankAccounts] = useState([]);
	const [amountVisibleIds, setAmountVisibleIds] = useState({});
	const [salarySlipSettings, setSalarySlipSettings] = useState({
		salary_slip_visibility_mode: "always_visible",
		salary_slip_visible_count: 12,
	});

	const formatCurrency = (amount) =>
		new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			maximumFractionDigits: 2,
		}).format(Number(amount || 0));

	const getMonthName = (monthNumber) => {
		const date = new Date();
		date.setMonth(Number(monthNumber || 1) - 1);
		return date.toLocaleString([], { month: "long" });
	};

	const normalizeText = (value = "") => String(value || "").trim().toLowerCase();

	const parseAmount = (amount) => Number(amount || 0);

	const salaryHeadMap = React.useMemo(
		() => new Map((salaryHeads || []).map((head) => [head.id, head])),
		[salaryHeads]
	);

	const isSalaryLineMatch = (line, keys) => {
		const head = salaryHeadMap.get(line.salary_head);
		const headValue = normalizeText(head?.head_value);
		const description = normalizeText(line.description);
		return keys.some((key) => headValue === key || description.includes(key));
	};

	const getFinancialYearRange = (offset = 0) => {
		const today = new Date();
		const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
		return {
			startYear: startYear + offset,
			endYear: startYear + offset + 1,
		};
	};

	const isSalaryInFinancialYear = (salary, range) => {
		const year = Number(salary.batch?.year);
		const month = Number(salary.batch?.month);
		return (year === range.startYear && month >= 4) || (year === range.endYear && month <= 3);
	};

	const handleSalaryDownload = async (rowData) => {
		const invoiceYear = rowData.batch.year;
		const invoiceMonth = rowData.batch.month;
		const invoiceID = rowData.id;
		const firstDownload = !rowData.salary_slip_download_count;
		if (
			firstDownload &&
			salarySlipSettings.salary_slip_visibility_mode === "hide_after_download" &&
			!window.confirm("If you download this salary slip now, it will no longer be visible here after download. Continue?")
		) {
			return;
		}
		try {
			const res = await authFetch(
				`https://api.flickhr.com/api/v1/payrole/salary-slip/${loggedUser.employee_id}/${invoiceYear}/${invoiceMonth}/${invoiceID}`,
				{}
			);

			if (!res.ok) {
				const text = await res.text();
				console.error("Download failed:", text);
				return;
			}

			const contentType = res.headers.get("content-type");
			if (!contentType?.includes("pdf")) {
				console.error("Expected PDF but got:", contentType);
				return;
			}

			const blob = await res.blob();
			const url = window.URL.createObjectURL(blob);

			const a = document.createElement("a");
			a.href = url;
			a.download = `SalarySlip_${invoiceMonth}_${invoiceYear}.pdf`;
			document.body.appendChild(a);
			a.click();

			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
			const now = new Date().toISOString();
			setSalaryRows((rows) =>
				rows.map((item) =>
					item.id === invoiceID
						? {
								...item,
								salary_slip_download_count: Number(item.salary_slip_download_count || 0) + 1,
								salary_slip_first_downloaded_at: item.salary_slip_first_downloaded_at || now,
								salary_slip_last_downloaded_at: now,
						  }
						: item
				)
			);
		} catch (err) {
			console.error("Error downloading salary slip:", err);
		}
	};

	useEffect(() => {
		getEmployeeContract();
		getBankAccounts();
		getSalaryHeads();
		getSalarySlipSettings();
		getSalarySlips();
	}, [])

	const accountInsights = React.useMemo(() => {
		const currentFy = getFinancialYearRange(0);
		const lastFy = getFinancialYearRange(-1);
		const sortedRows = [...salaryRows].sort((a, b) => {
			const aKey = `${a.batch?.year || 0}${String(a.batch?.month || 0).padStart(2, "0")}`;
			const bKey = `${b.batch?.year || 0}${String(b.batch?.month || 0).padStart(2, "0")}`;
			return bKey.localeCompare(aKey);
		});
		const initial = {
			totalTds: 0,
			currentFyBasic: 0,
			lastFyBasic: 0,
			pfDeposits: [],
		};
		return sortedRows.reduce((summary, salary) => {
			let monthlyPf = 0;
			(salary.salary_lines || []).forEach((line) => {
				const amount = parseAmount(line.amount);
				if (isSalaryLineMatch(line, ["tds"])) summary.totalTds += amount;
				if (isSalaryLineMatch(line, ["basic"])) {
					if (isSalaryInFinancialYear(salary, currentFy)) summary.currentFyBasic += amount;
					if (isSalaryInFinancialYear(salary, lastFy)) summary.lastFyBasic += amount;
				}
				if (isSalaryLineMatch(line, ["epf", "pf"])) monthlyPf += amount;
			});
			if (monthlyPf) {
				summary.pfDeposits.push({
					id: salary.id,
					month: getMonthName(salary.batch?.month),
					year: salary.batch?.year,
					amount: monthlyPf,
				});
			}
			return summary;
		}, initial);
	}, [salaryRows, salaryHeadMap]);

	const activeBankAccount = React.useMemo(
		() => bankAccounts.find((bank) => bank.is_primary) || employeeContract?.bank || bankAccounts[0],
		[bankAccounts, employeeContract]
	);

	const inactiveBankAccounts = React.useMemo(
		() => bankAccounts.filter((bank) => activeBankAccount?.id && bank.id !== activeBankAccount.id),
		[bankAccounts, activeBankAccount]
	);

	const visibleSalaryRows = React.useMemo(() => {
		const sortedRows = [...salaryRows].sort((a, b) => {
			const aKey = `${a.batch?.year || 0}${String(a.batch?.month || 0).padStart(2, "0")}`;
			const bKey = `${b.batch?.year || 0}${String(b.batch?.month || 0).padStart(2, "0")}`;
			return bKey.localeCompare(aKey);
		});
		if (salarySlipSettings.salary_slip_visibility_mode === "hide_after_download") {
			return sortedRows.filter((row) => !row.salary_slip_download_count);
		}
		if (salarySlipSettings.salary_slip_visibility_mode === "last_x") {
			return sortedRows.slice(0, Number(salarySlipSettings.salary_slip_visible_count || 12));
		}
		return sortedRows;
	}, [salaryRows, salarySlipSettings]);

	const salarySlips = React.useMemo(() => ({
		columns: [
			{
				Header: "NAME",
				accessor: "name",
				Cell: ({ row }) => (
					<div style={{ display: "flex", alignItems: "center" }}>
						<span style={{}}>{row?.original?.contract?.employee?.user?.firstname}</span>
						<span style={{ marginLeft: "0.2rem" }}>
							{row?.original?.contract?.employee?.user?.lastname}
						</span>
					</div>
				),
			},
			{
				Header: "MONTH",
				accessor: "salary_month",
				Cell: ({ row }) => (
					<MDTypography variant="button" fontWeight="medium">
						{getMonthName(row.original?.batch?.month)}
					</MDTypography>
				),
			},
			{
				Header: "YEAR",
				accessor: "salary_year",
				Cell: ({ row }) => (
					<MDTypography variant="button" fontWeight="medium">
						{row.original?.batch?.year || "-"}
					</MDTypography>
				),
			},
			//{
			//	Header: "PAY PERIOD",
			//	accessor: "month",
			//	Cell: ({ row }) => {
      //    const { month, year, month_choices } = row.original.batch || {};

      //    return (
      //      <Stack spacing={0.25}>
			//				<MDTypography variant="button" fontWeight="medium">{month_choices?.[month] || getMonthName(month)} {year}</MDTypography>
			//				<MDTypography variant="caption" color="text">Salary slip</MDTypography>
      //      </Stack>
      //    )
      //  }
      //},
			{
				Header: "TOTAL WORKING DAYS",
				accessor: "total_days"
			},
			{
				Header: "WORKING DAYS",
				accessor: "working_days"
			},
			{
				Header: "Absent",
				accessor: "absent_days"
			},
			{
				Header: "PAID LEAVE",
				accessor: "paid_leave"
			},
			{
				Header: "UNPAID LEAVE",
				accessor: "unpaid_leave"
			},
			{
				Header: "NET AMOUNT",
				accessor: "net_pay",
				Cell: ({ row }) => {
					const visible = amountVisibleIds[row.original.id];
					return (
						<Stack direction="row" spacing={1} alignItems="center">
							<MDTypography variant="button" fontWeight="medium">
								{visible ? formatCurrency(row.original.net_pay) : "********"}
							</MDTypography>
							<Tooltip title={visible ? "Hide amount" : "Show amount"}>
								<IconButton
									size="small"
									onClick={() =>
										setAmountVisibleIds((state) => ({
											...state,
											[row.original.id]: !state[row.original.id],
										}))
									}
								>
									{visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
								</IconButton>
							</Tooltip>
						</Stack>
					);
				}
			},
			{
				Header: "DETAILS",
				accessor: "inout",
				Cell: ({ row }) => (
					<div style={{ display: "flex", alignItems: "center" }}>
						<Tooltip title="Info">
							<IconButton
								aria-label="info"
								size="small"
								onClick={() => {
									setSalaryLineDialog(true)
									setSalaryDetails(row.original)
									//handleView(row.original.attendance_lists);
								}}
							>
								<Info />
							</IconButton>
						</Tooltip>
					</div>
				),
			},
			{
				Header: "DOWNLOAD",
				accessor: "download",
				Cell: ({ row }) => {
					const downloaded = Number(row.original.salary_slip_download_count || 0) > 0;

					return (
						<Stack direction="row" spacing={1} alignItems="center">
							{downloaded && (
								<Chip
									size="small"
									color="success"
									variant="outlined"
									icon={<CheckCircle />}
									label={`Downloaded${row.original.salary_slip_download_count > 1 ? ` x${row.original.salary_slip_download_count}` : ""}`}
								/>
							)}
							<Tooltip title={downloaded ? "Download again" : "Download salary slip"}>
								<IconButton
									aria-label="download"
									size="small"
									onClick={() => handleSalaryDownload(row.original)}
								>
									<Download ></Download>
								</IconButton>
							</Tooltip>
						</Stack>
					);
				},
			},
		],
		rows: visibleSalaryRows,
	}), [visibleSalaryRows, amountVisibleIds, salarySlipSettings]);

	async function getEmployeeContract() {
		var res = await ApiCalls.getEmployeeContract(`?employee_id=${loggedUser.employee_id}&is_active=true`);
		if (res.success && res.code == 200) {
			setEmployeeContract(res?.data[0]);
			setAccountNumber("XXXXXXXXXXXX" + (res?.data[0]?.bank?.bank_account?.substr(res?.data[0]?.bank?.bank_account?.length - 4) ?? "XXXX"))
		}
	}

	async function getBankAccounts() {
		var res = await ApiCalls.getEmployeeBankDetails(`?employee=${loggedUser.employee_id}`);
		if (res.success && res.code === 200) {
			setBankAccounts(res.data || []);
		}
	}

	async function getSalaryHeads() {
		var res = await ApiCalls.getSalaryHead();
		if (res.success && res.code === 200) {
			setSalaryHeads(res.data || []);
		}
	}

	async function getSalarySlips() {
		var res = await ApiCalls.getSalary(`?employee=${loggedUser.employee_id}`);
		if (res.success && res.code === 200) {
			setSalaryRows(res.data);
		}
	}

	async function getSalarySlipSettings() {
		const res = await ApiCalls.getOrgConfigCurrent();
		if (res.success && res.code === 200) {
			setSalarySlipSettings({
				salary_slip_visibility_mode: res.data.salary_slip_visibility_mode || "always_visible",
				salary_slip_visible_count: res.data.salary_slip_visible_count || 12,
			});
		}
	}

	const renderSalaryInfoDialog = () => {
		return(
			<Dialog open={salaryLineDialog} onClose={() => setSalaryLineDialog(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Salary Details</DialogTitle>
				<DialogContent dividers>
					{salaryDetails?.salary_lines?.map((item, index) => (
						<Box key={index} sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
							<Typography variant="subtitle1">{item.description}</Typography>
							<Typography variant="subtitle1">{formatCurrency(item.amount)}</Typography>
						</Box>
					))}

					<Divider sx={{ my: 2 }} />

					<MDBox sx={{display : "flex", justifyContent: "space-between"}}>
						<MDTypography variant='h6'>Total</MDTypography>
						<MDTypography variant='h6'>{formatCurrency(salaryDetails?.net_pay)}</MDTypography>
					</MDBox>
				</DialogContent>
				<DialogActions>
					<MDButton onClick={() => setSalaryLineDialog(false)} color="primary">
						Close
					</MDButton>
				</DialogActions>
			</Dialog>
		)
	}

	return (
		<>
			{props?.action === 'SalarySlips' ?
				<MDBox pt={2}>
					<MDBox px={1} pb={2}>
						<MDTypography variant="h5">Salary Slips</MDTypography>
						<MDTypography variant="button" color="text">
							Amounts are hidden by default. Use the eye icon to reveal a row.
						</MDTypography>
					</MDBox>
					<DataTable table={salarySlips}></DataTable>
				</MDBox>
				: <Grid container spacing={2}>
					<Grid item xs={12} lg={6} sm={6}>
						<MasterCard
							number={activeBankAccount?.bank_account ? `XXXXXXXXXXXX${activeBankAccount.bank_account.substr(activeBankAccount.bank_account.length - 4)}` : accountNumber ?? "XXXXXXXXXXXXXXXX"}
							accountHolder={activeBankAccount?.name_in_account}
							bankName={activeBankAccount?.bank_name}
						/>
					</Grid>

					<Grid item xs={12} lg={6} sm={6}>
						<Card sx={{ height: "100%", p: 2.5, borderRadius: "18px" }}>
							<Stack spacing={1.25}>
								<MDTypography variant="h6">Bank Accounts</MDTypography>
								<Stack direction="row" spacing={1} alignItems="center">
									<Chip size="small" color="success" label="Active" />
									<MDTypography variant="button" fontWeight="medium">
										{activeBankAccount?.bank_name || "-"} {activeBankAccount?.bank_account ? `****${activeBankAccount.bank_account.substr(activeBankAccount.bank_account.length - 4)}` : ""}
									</MDTypography>
								</Stack>
								<Divider />
								<MDTypography variant="caption" color="text">Inactive / alternate accounts</MDTypography>
								{inactiveBankAccounts.length ? inactiveBankAccounts.map((bank) => (
									<Stack key={bank.id} direction="row" spacing={1} alignItems="center">
										<Chip size="small" variant="outlined" label="Inactive" />
										<MDTypography variant="caption">
											{bank.bank_name || "-"} {bank.bank_account ? `****${bank.bank_account.substr(bank.bank_account.length - 4)}` : ""}
										</MDTypography>
									</Stack>
								)) : (
									<MDTypography variant="caption" color="text">No alternate bank accounts found.</MDTypography>
								)}
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} lg={6} sm={6} >
						<MDBox sx = {{ height : "100%"}}>
							<DefaultInfoCard
								icon="account_balance"
								title="Current CTC"
								description="Active employee contract"
								value={formatCurrency(employeeContract?.ctc)}
								hidden={true}
							/>
						</MDBox>
					</Grid>
					<Grid item xs={12} lg={6} sm={6}>
						<DefaultInfoCard
							icon="event_available"
							title="Next Increment / Review"
							description="Based on active contract end date"
							value={employeeContract?.end_date ?? "Not scheduled"}
						/>
					</Grid>

					<Grid item xs={12} lg={3} sm={6}>
						<DefaultInfoCard
							icon="receipt_long"
							title="TDS Deducted Till Now"
							description="From available salary slips"
							value={formatCurrency(accountInsights.totalTds)}
							hidden={true}
						/>
					</Grid>
					<Grid item xs={12} lg={3} sm={6}>
						<DefaultInfoCard
							icon="payments"
							title="Basic Paid Current FY"
							description={`${getFinancialYearRange(0).startYear}-${getFinancialYearRange(0).endYear}`}
							value={formatCurrency(accountInsights.currentFyBasic)}
							hidden={true}
						/>
					</Grid>
					<Grid item xs={12} lg={3} sm={6}>
						<DefaultInfoCard
							icon="history"
							title="Basic Paid Last FY"
							description={`${getFinancialYearRange(-1).startYear}-${getFinancialYearRange(-1).endYear}`}
							value={formatCurrency(accountInsights.lastFyBasic)}
							hidden={true}
						/>
					</Grid>
					<Grid item xs={12} lg={3} sm={6}>
						<DefaultInfoCard
							icon="badge"
							title="PF Account"
							description="PF number is not exposed in this payroll API yet"
							value={employeeContract?.employee?.personal_info?.pf_number || "Not configured"}
							hidden={true}
						/>
					</Grid>

					<Grid item xs={12} lg={6} sm={6}>
						<Card sx={{ height: "100%", p: 2.5, borderRadius: "18px" }}>
							<Stack spacing={1.5}>
								<MDTypography variant="h6">Last 3 PF Deductions</MDTypography>
								{accountInsights.pfDeposits.slice(0, 3).length ? accountInsights.pfDeposits.slice(0, 3).map((item) => (
									<MDBox key={item.id} display="flex" justifyContent="space-between" alignItems="center">
										<MDTypography variant="button" color="text">{item.month} {item.year}</MDTypography>
										<MDTypography variant="button" fontWeight="medium">{formatCurrency(item.amount)}</MDTypography>
									</MDBox>
								)) : (
									<MDTypography variant="button" color="text">No PF deduction lines found in recent salary slips.</MDTypography>
								)}
							</Stack>
						</Card>
					</Grid>

					<Grid item xs={12} lg={6} sm={6}>
						<SalaryComponentChart salaryDetail={employeeContract?.salary_details} />
					</Grid>
				</Grid>}
				{salaryLineDialog && renderSalaryInfoDialog()}
		</>

	)
}

export default SalaryAccounts;
